/**
 * DTOs do sub-recurso `companies/:companyId/documentos-fiscais`.
 *
 * Contrato: card Trello [AA0OZ73A] Fase A, pacotes _03/_04/_05 da Genie.
 * BFF wiring: `apps/bff/src/services/genexus-routes.ts` (resource
 * `company-documentos-fiscais`, `compositeIdParts: ['modelo', 'serie']`).
 *
 * PK composta `(modelo, serie)`: no contrato com o front, viaja como string
 * `"<modelo>/<serie>"` no campo `id` (encadeado por `/`), o que casa
 * exatamente com o segmento de URL `/{modelo}/{serie}` que o BFF espera.
 *
 * DELETE físico — sem soft-delete. Bloqueio só quando há NF Empresa emitida
 * vinculada ao par modelo+serie (verificação no upstream).
 */

/** Texto Livre (T) | Requisição de Compra (R). Domain Char/Enum no upstream. */
export type DescricaoNF = 'T' | 'R';

/** Domain `TipoIntegracaoNF` (Enum VarChar(3)) no upstream. Nullable no contrato. */
export type TipoIntegracaoNF = 'INN' | 'WSN' | 'WSE' | 'WSM';

export interface CompanyDocumentoFiscalDto {
  /** `"<modelo>/<serie>"` — usado como TId nos hooks CRUD. */
  id: string;
  modelo: number;
  serie: string;
  descricaoNF: DescricaoNF | null;
  ambienteNF: number;
  tipoIntegracaoNF: TipoIntegracaoNF | null;
  tributacaoServico: number | null;
  municipioTributacao: string | null;
  nbsCodigo: string | null;
  ibsIndicadorOperacao: string | null;
  ibsSituacaoTributaria: string | null;
  ibsClassificacaoTributaria: string | null;
  cstSituacaoTributariaPisCofins: string | null;
  tipoRetencaoPisCofins: string | null;
  criadoEm: string;
  criadoPorGuid: string;
  criadoPorNome: string;
  atualizadoEm: string | null;
  atualizadoPorGuid: string | null;
  atualizadoPorNome: string | null;
}

export interface CompanyDocumentoFiscalListItem {
  id: string;
  modelo: number;
  serie: string;
  descricaoNF: DescricaoNF | null;
  ambienteNF: number;
  tipoIntegracaoNF: TipoIntegracaoNF | null;
}

/**
 * Input flat: modelo+serie compõem a PK e só são editáveis na criação.
 * Na edição, o form bloqueia esses dois campos.
 */
export interface CompanyDocumentoFiscalInput {
  modelo: number;
  serie: string;
  descricaoNF: DescricaoNF;
  ambienteNF: number;
  tipoIntegracaoNF?: TipoIntegracaoNF | null;
  tributacaoServico?: number | null;
  municipioTributacao?: string | null;
  nbsCodigo?: string | null;
  ibsIndicadorOperacao?: string | null;
  ibsSituacaoTributaria?: string | null;
  ibsClassificacaoTributaria?: string | null;
  cstSituacaoTributariaPisCofins?: string | null;
  tipoRetencaoPisCofins?: string | null;
}

/** Helper: monta o `id` encadeado a partir do par modelo+serie. */
export function makeDocumentoFiscalId(modelo: number, serie: string): string {
  return `${modelo}/${serie}`;
}
